/**
 * useMyGoals — fetch the calling user's sprint and BOY goals plus actuals.
 *
 * sprint_goal and boy_goal are stored on public.users.
 * Actuals (sprintActual, boyActual, etc.) are reserved for a future CRM
 * integration via external_opportunities once owner mapping is available.
 */
import { useEffect, useState } from "react";
import { supabase, getSession, getCurrentTenant } from "@/lib/session";
import { useSprintConfig } from "./useSprintConfig";

export interface MyGoals {
  sprintGoal: number;
  boyGoal: number;
  sprintActual: number;     // closed-won inside sprint window
  sprintCommitted: number;  // stage>=Commit, expected_close inside sprint
  boyActual: number;        // closed-won after sprint, this calendar year
  boyCommitted: number;     // stage>=Commit, expected_close after sprint, this year
}

const empty: MyGoals = {
  sprintGoal: 0, boyGoal: 0,
  sprintActual: 0, sprintCommitted: 0,
  boyActual: 0, boyCommitted: 0,
};

export function useMyGoals() {
  const session = getSession();
  const tenant = getCurrentTenant();
  const { config } = useSprintConfig();
  const [goals, setGoals] = useState<MyGoals>(empty);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.id || !tenant?.id) {
      setGoals(empty);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const sb = supabase();

      // Step 1: read sprint_goal + boy_goal directly from users.
      let sprintGoal = 0, boyGoal = 0;
      try {
        const { data } = await sb
          .from("users")
          .select("sprint_goal, boy_goal")
          .eq("id", session.id)
          .limit(1);
        if (data && data[0]) {
          sprintGoal = Number((data[0] as any).sprint_goal ?? 0);
          boyGoal = Number((data[0] as any).boy_goal ?? 0);
        }
      } catch { /* missing column tolerated until migration applied */ }

      // Step 2: actuals from CRM — reserved for future external_opportunities
      // integration once owner_user_id mapping is available. Returns zeros until then.
      const sprintActual = 0, sprintCommitted = 0, boyActual = 0, boyCommitted = 0;

      if (cancelled) return;
      setGoals({ sprintGoal, boyGoal, sprintActual, sprintCommitted, boyActual, boyCommitted });
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session?.id, tenant?.id, config?.sprintEnd, config?.sprintStart]);

  return { goals, loading };
}
